'use client'

import { useEffect, useRef } from 'react'

const TURNSTILE_SITE_KEY = '0x4AAAAAAEv8VVv2vDbTPsZx'
const TURNSTILE_SCRIPT =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        parameters: {
          sitekey: string
          action?: string
          callback: (token: string) => void
          'expired-callback': () => void
          'error-callback': () => void
        }
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId: string) => void
    }
  }
}

type CloudflareTurnstileProps = {
  onChange: (token: string) => void
  resetKey?: number
  action?: string
}

export function CloudflareTurnstile({
  onChange,
  resetKey = 0,
  action,
}: CloudflareTurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string>()
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | undefined

    const renderWidget = () => {
      if (
        cancelled ||
        !containerRef.current ||
        !window.turnstile ||
        widgetIdRef.current !== undefined
      )
        return

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action,
        callback: (token) => onChangeRef.current(token),
        'expired-callback': () => onChangeRef.current(''),
        'error-callback': () => onChangeRef.current(''),
      })
      clearInterval(timer)
    }

    if (!document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`)) {
      const script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT
      script.async = true
      script.defer = true
      script.onload = renderWidget
      document.head.appendChild(script)
    }

    renderWidget()
    timer = setInterval(renderWidget, 250)

    return () => {
      cancelled = true
      clearInterval(timer)
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = undefined
      }
    }
  }, [action])

  useEffect(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
      onChangeRef.current('')
    }
  }, [resetKey])

  return <div ref={containerRef} />
}
